import { WhereItIs } from "./WhereItIs";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AuthImg } from "@/components/AuthImg";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { AlertOctagon, ArrowRight, ImageOff, Loader2, MapPin, ShieldCheck, Sparkles, Truck } from "lucide-react";

// RED TWIN VIEW — tap the red number, see both, decide in five seconds.
//
// HIS WORDS, 2026-10-13: *"one tap on a red number shows the look-alike and the part holding it,
// side by side, so it's settled in five seconds."* Red already told the crew WHICH number this read
// nearly is; it could not tell them WHAT that number is on. A man had to walk to FAS-003262 to find
// out whether he was holding the same part misread or a genuinely different one.
//
// IT DECIDES NOTHING. Both buttons hand the decision back to the controls that already exist: the
// correction puts the held number in the row's own edit box (and the crew member still ✓verifies
// it), and "a different part" just closes — the red clears when a person verifies, exactly as today.
export function RedTwinView({ partId, value, open, onOpenChange, onUseTwin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [shot, setShot] = useState(null);     // the one photo viewer, from either side

  useEffect(() => {
    if (!open || !partId || !value) return;
    setLoading(true);
    api.get(`/parts/${partId}/red-twin`, { params: { value } })
      .then((r) => setData(r.data))
      .catch(() => setData({ twin: null, reason: "couldn't look it up" }))
      .finally(() => setLoading(false));
  }, [open, partId, value]);

  const t = data?.twin;
  const card = "rounded-2xl border p-4 flex flex-col gap-3";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" data-testid="red-twin-view" className="h-kb max-h-kb overflow-y-auto rounded-t-3xl">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2 text-[#B01E28]">
            <AlertOctagon className="w-5 h-5" />Is this the same part, misread?
          </SheetTitle>
        </SheetHeader>

        {loading && (
          <div data-testid="red-twin-loading" className="py-16 text-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />Looking on the shelves…
          </div>
        )}

        {!loading && !t && (
          <div data-testid="red-twin-none" className="py-12 text-center text-slate-500 text-sm">
            Nothing on the shelves is one character off {value}. {data?.reason ? `(${data.reason})` : ""}
          </div>
        )}

        {!loading && t && (
          <div className="mt-3 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
              {/* WHAT HE JUST READ */}
              <div data-testid="red-twin-mine" className={`${card} border-slate-300 bg-white`}>
                <div className="text-[11px] font-bold uppercase tracking-widest text-slate-500">The number just read</div>
                <div data-testid="red-twin-mine-value" className="font-mono text-3xl font-black text-slate-900 break-all">{data.value}</div>
                <div className="text-sm text-slate-600">{data.mine?.name || "this part"}</div>
                <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-500">
                  {data.mine?.short_code && <span className="font-mono font-bold">{data.mine.short_code}</span>}
                  {data.mine?.part_type_name && <Badge className="rounded-full border-0 bg-slate-100 text-slate-600 text-[10px]">{data.mine.part_type_name}</Badge>}
                  <Badge className="rounded-full border border-violet-200 bg-violet-50 text-violet-700 text-[10px]"><Sparkles className="w-3 h-3 mr-0.5" />read by the AI</Badge>
                </div>
                {/* COMPARING TWO PARTS MEANS LOOKING AT THEM. Both photos open the one viewer —
                    zoom, rotate, drag (2026-10-15): this screen exists to settle a misread. */}
                <button type="button" data-testid="red-twin-mine-photo" disabled={!data.mine?.photo_file_id}
                  onClick={() => data.mine?.photo_file_id && setShot([{ file_id: data.mine.photo_file_id }])}
                  className="rounded-xl overflow-hidden bg-slate-100 aspect-[4/3] flex items-center justify-center disabled:cursor-default">
                  {data.mine?.photo_file_id
                    ? <AuthImg fileId={data.mine.photo_file_id} thumb className="w-full h-full object-cover" alt="the part in hand" />
                    : <ImageOff className="w-6 h-6 text-slate-300" />}
                </button>
              </div>

              <div className="hidden md:flex flex-col items-center justify-center px-1">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#B01E28] text-center leading-tight">
                  {data.read_char} here<br />
                  <ArrowRight className="w-4 h-4 mx-auto my-1" />
                  {data.could_be} there
                </div>
              </div>

              {/* WHAT WE ALREADY HOLD */}
              <div data-testid="red-twin-held" className={`${card} border-[#B01E28]/40 bg-red-50/40`}>
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#B01E28]">Already on the shelves</div>
                <div data-testid="red-twin-held-value" className="font-mono text-3xl font-black text-slate-900 break-all">{t.value}</div>
                <div className="text-sm text-slate-800 font-semibold">{t.name || "(no name yet)"}</div>
                <div className="flex items-center gap-2 flex-wrap text-[11px] text-slate-600">
                  <a data-testid="red-twin-open-part" href={`/parts/${t.part_id}`} target="_blank" rel="noreferrer"
                    className="font-mono font-bold underline underline-offset-2">{t.short_code}</a>
                  {t.part_type_name && <Badge className="rounded-full border-0 bg-slate-200 text-slate-700 text-[10px]">{t.part_type_name}</Badge>}
                  {t.status && <Badge className="rounded-full border-0 bg-slate-500 text-white text-[10px]">{t.status.replace(/_/g, " ")}</Badge>}
                </div>
                <button type="button" data-testid="red-twin-held-photo" disabled={!t.photo_file_id}
                  onClick={() => t.photo_file_id && setShot([{ file_id: t.photo_file_id }])}
                  className="rounded-xl overflow-hidden bg-slate-100 aspect-[4/3] flex items-center justify-center disabled:cursor-default">
                  {t.photo_file_id
                    ? <AuthImg fileId={t.photo_file_id} thumb className="w-full h-full object-cover" alt={t.name || "the part we hold"} />
                    : <ImageOff className="w-6 h-6 text-slate-300" />}
                </button>
                <div className="space-y-1 text-[11px] text-slate-600">
                  {t.location && <div className="flex items-center gap-1.5"><WhereItIs location={t.location} size="sm" testid="red-twin-held-location" /></div>}
                  {t.donor && <div className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-slate-400" />{t.donor.truck} · {t.donor.short_code}</div>}
                  {t.number_verified_at
                    ? <div className="flex items-center gap-1.5 text-emerald-700"><ShieldCheck className="w-3.5 h-3.5" />that number was checked by a person</div>
                    : <div className="flex items-center gap-1.5 text-violet-700"><Sparkles className="w-3.5 h-3.5" />that number was read by the AI and not checked</div>}
                  {t.number_group_label && <div className="text-slate-500">read off the {t.number_group_label} tag</div>}
                </div>
              </div>
            </div>

            {/* WHAT THE MACHINE CAN SAY, PLAINLY */}
            <div data-testid="red-twin-why" className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-[12px] text-slate-700 space-y-1">
              <div><b>{data.read_char}</b> where the other says <b>{data.could_be}</b> — {data.digit_slip ? "a digit read as another digit, the kind that is usually a misread." : "a character that only looks similar."}</div>
              <div>{data.same_part_type
                ? "It is the SAME kind of part, which is why this one stops the line."
                : "It is a different kind of part, so it may well be a coincidence."}</div>
              {data.other_look_alikes > 0 && (
                <div data-testid="red-twin-others">And {data.other_look_alikes} other number{data.other_look_alikes === 1 ? "" : "s"} on the shelves {data.other_look_alikes === 1 ? "is" : "are"} this close too — a crowded family.</div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              {onUseTwin && (
                <Button data-testid="red-twin-use-held" onClick={() => { onUseTwin(t.value); onOpenChange(false); }}
                  className="rounded-xl h-12 flex-1 bg-[#B01E28] hover:bg-[#8f1820] text-white text-sm font-bold">
                  It's a misread — use {t.value}
                </Button>
              )}
              <Button data-testid="red-twin-different" variant="outline" onClick={() => onOpenChange(false)}
                className="rounded-xl h-12 flex-1 text-sm font-bold">
                A different part — keep {data.value}
              </Button>
            </div>
            <div className="text-[11px] text-slate-500 pb-4">
              Either way nothing is confirmed yet: the red clears when you ✓verify the number against the stamp.
            </div>
          </div>
        )}
      </SheetContent>
      <PhotoLightbox photos={shot || []} index={shot ? 0 : null} onIndex={() => {}}
        onClose={() => setShot(null)} testidPrefix="red-twin-photo" />
    </Sheet>
  );
}
